import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

interface AsyncState<T> {
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
  reload(): void;
}

/**
 * Minimalny hook do pobierania danych. Świadomie bez biblioteki typu React Query:
 * dashboard 1.0 ma kilka ekranów odczytu, a każda dodatkowa zależność to koszt
 * utrzymania i rozmiar bundla (§49). Gdy pojawi się cache współdzielony między
 * ekranami, wtedy będzie uzasadnienie, żeby ją dodać.
 */
export function useApi<T>(path: string | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api<T>(path)
      .then((result) => {
        // Odpowiedź na porzucone żądanie (np. po zmianie organizacji) musi zostać
        // zignorowana, inaczej na ekranie pojawiłyby się dane poprzedniego tenanta.
        if (!cancelled) setData(result);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, reload };
}
